import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { API_BASE } from './api';
import { useAuth } from './hooks/useAuth';
import { usePlayer } from './hooks/usePlayer';
import { useChat } from './hooks/useChat';
import Sidebar from './components/Sidebar';
import PlayerBar from './components/PlayerBar';
import SongRow from './components/SongRow';
import AuthView from './components/AuthView';
import AdminView from './components/AdminView';
import ChatView from './components/ChatView';
import { ArrowLeft, Home, Library, ListMusic, Mic2, Search, UserRound, Music2 } from 'lucide-react';

function App() {
  const auth = useAuth();
  const player = usePlayer(auth.token);
  const chat = useChat(auth.token);
  const [activeView, setActiveView] = useState(localStorage.getItem('nas-view') || 'home');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [activePlaylist, setActivePlaylist] = useState(null);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedPlaylistSongs, setSelectedPlaylistSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [theme, setThemeState] = useState(localStorage.getItem('minmedia-theme') || 'blue');

  const setTheme = useCallback((t) => {
    setThemeState(t);
    localStorage.setItem('minmedia-theme', t);
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    root.className = theme === 'blue' ? '' : `theme-${theme}`;
  }, [theme]);

  const fetchPlaylists = useCallback(async () => {
    if (!auth.token) return;
    try {
      const res = await fetch(`${API_BASE}/api/playlists`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) {
        throw new Error(data?.error || `Oväntat svar från servern (HTTP ${res.status})`);
      }
      setPlaylists(data);
    } catch (err) {
      console.error('Error fetching playlists:', err);
    }
  }, [auth.token]);

  const fetchPlaylistSongs = useCallback(async (playlist) => {
    if (!auth.token || !playlist) return;
    try {
      const res = await fetch(`${API_BASE}/api/playlists/${playlist.id}`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      const data = await res.json();
      if (!res.ok || !data || !Array.isArray(data.songs)) {
        throw new Error(data?.error || 'Oväntat svar från servern');
      }
      setActivePlaylist(playlist);
      setSelectedPlaylist(playlist);
      setSelectedPlaylistSongs(data.songs);
      setActiveView('playlist-detail');
    } catch (err) {
      console.error('Error fetching playlist songs:', err);
    }
  }, [auth.token]);

  useEffect(() => {
    if (auth.token) fetchPlaylists();
  }, [auth.token, fetchPlaylists]);

  useEffect(() => {
    chat.chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.chatMessages, activeView]);

  useEffect(() => {
    if (activeView === 'admin' && !auth.isAdmin) {
      setActiveView('home');
    }
  }, [activeView, auth.isAdmin]);

  const downloadSong = useCallback(async (song) => {
    try {
      const res = await fetch(`${API_BASE}/api/download?fileName=${encodeURIComponent(song.fileName)}`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (!res.ok) throw new Error('Nedladdning misslyckades');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = song.fileName.split('/').pop().split('\\').pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      alert(err.message);
    }
  }, [auth.token]);

  const addSongToPlaylist = useCallback(async (song) => {
    const plName = prompt("Vilken spellista?");
    if (!plName) return;
    const pl = playlists.find(p => p.name.toLowerCase() === plName.toLowerCase());
    if (!pl) { alert("Hittades inte!"); return; }
    try {
      const res = await fetch(`${API_BASE}/api/playlists/${pl.id}/songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
        body: JSON.stringify({ songFileName: song.fileName })
      });
      if (res.ok) alert("Lagd till!");
    } catch (err) {
      alert("Ett fel uppstod");
    }
  }, [playlists, auth.token]);

  const removeSongFromPlaylist = useCallback(async (song) => {
    if (!selectedPlaylist) return;
    try {
      const res = await fetch(`${API_BASE}/api/playlists/${selectedPlaylist.id}/songs`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
        body: JSON.stringify({ songFileName: song.fileName })
      });
      if (res.ok) {
        setSelectedPlaylistSongs(prev => prev.filter(f => f !== song.fileName));
      }
    } catch (err) {
      console.error('Error removing song from playlist:', err);
    }
  }, [selectedPlaylist, auth.token]);

  if (!auth.token) {
    return <AuthView {...auth} />;
  }

  const renderSongList = (filteredSongs, { removeFromPlaylist: removeHandler } = {}) => (
    <>
      <div className="list-header">
        <span>#</span><span>Titel</span><span>Artist</span><span>Album</span><span style={{ width: '80px' }}>Tid</span><span style={{ width: '80px', textAlign: 'right' }}>Lägg till</span>
      </div>
      <div className="songs-list">
        {filteredSongs.map((song) => (
          <SongRow
            key={song.fileName}
            song={song}
            index={player.songs.indexOf(song)}
            currentSongIndex={player.currentSongIndex}
            playSong={player.playSong}
            downloadSong={downloadSong}
            addSongToPlaylist={addSongToPlaylist}
            setSelectedArtist={setSelectedArtist}
            setActiveView={setActiveView}
            removeFromPlaylist={removeHandler}
          />
        ))}
      </div>
    </>
  );

  const filterSongs = (q) => player.sortedSongs.filter(s =>
    s.title.toLowerCase().includes(q.toLowerCase()) || s.artist.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="app-container">
      <Sidebar
        activeView={activeView} setActiveView={setActiveView}
        setSelectedArtist={setSelectedArtist}
        playlists={playlists} fetchPlaylists={fetchPlaylists}
        token={auth.token} username={auth.username} isAdmin={auth.isAdmin}
        handleLogout={auth.handleLogout}
        songs={player.songs} refreshSongs={player.refreshSongs}
        theme={theme} setTheme={setTheme}
        fetchPlaylistSongs={fetchPlaylistSongs}
        activePlaylist={activePlaylist}
      />

      <div className={`main-content ${activeView === 'chat' ? 'chat-active' : ''}`}>
        {activeView === 'home' && (
          <>
            <header className="library-header">
              <div className="library-icon"><Home /></div>
              <h2 style={{ flex: 1 }}>Hem</h2>
              <div className="search-container">
                <Search className="search-icon" />
                <input type="text" placeholder="Sök på hemsidan..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="search-input" />
              </div>
            </header>
            {renderSongList(filterSongs(searchQuery))}
          </>
        )}

        {activeView === 'library' && (
          <>
            <header className="library-header">
              <div className="library-icon"><Library /></div>
              <h2 style={{ flex: 1 }}>Ditt bibliotek</h2>
              <div className="search-container">
                <Search className="search-icon" />
                <input type="text" placeholder="Sök i biblioteket..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="search-input" />
              </div>
            </header>
            {renderSongList(filterSongs(searchQuery))}
          </>
        )}

        {activeView === 'all-songs' && (
          <>
            <header className="library-header">
              <div className="library-icon"><ListMusic /></div>
              <h2 style={{ flex: 1 }}>Alla låtar</h2>
              <div className="search-container">
                <Search className="search-icon" />
                <input type="text" placeholder="Sök i listan..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="search-input" />
              </div>
            </header>
            {renderSongList(filterSongs(searchQuery))}
          </>
        )}

        {activeView === 'artists' && (
          <>
            <header className="library-header">
              <div className="library-icon"><Mic2 /></div>
              <h2 style={{ flex: 1 }}>Artister</h2>
            </header>
            <div className="songs-list">
              {Array.from(new Set(player.songs.map(s => s.artist))).sort((a, b) => a.localeCompare(b, 'sv')).map((artist, index) => (
                <div key={artist} className={`song-list-item ${selectedArtist === artist ? 'active' : ''}`} onClick={() => { setSelectedArtist(artist); setActiveView('artist-detail'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '50px' }}>
                    <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(artist)}&background=random&color=fff&size=30`} alt={artist} style={{ width: '30px', height: '30px', borderRadius: '4px' }} />
                    <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>{index + 1}</span>
                  </div>
                  <span style={{ fontWeight: 'bold' }}>{artist}</span>
                  <span></span><span></span><span></span><span></span>
                </div>
              ))}
            </div>
          </>
        )}

        {activeView === 'artist-detail' && (
          <>
            <header className="library-header">
              <div className="library-icon"><UserRound /></div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0 }}>{selectedArtist}</h2>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Låtar av {selectedArtist}</div>
              </div>
              <button onClick={() => setActiveView('artists')} className="back-button"><ArrowLeft size={18} /> Tillbaka</button>
            </header>
            {renderSongList(player.sortedSongs.filter(s => s.artist === selectedArtist))}
          </>
        )}

        {activeView === 'playlist-detail' && selectedPlaylist && (
          <>
            <header className="library-header">
              <div className="library-icon"><Music2 /></div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0 }}>{selectedPlaylist.name}</h2>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{selectedPlaylistSongs.length} låtar</div>
              </div>
              <button onClick={() => setActiveView('library')} className="back-button"><ArrowLeft size={18} /> Tillbaka</button>
            </header>
            {selectedPlaylistSongs.length === 0 ? (
              <div className="empty-view">
                <Music2 size={48} strokeWidth={1.4} style={{ marginBottom: '20px' }} />
                <h2>Ingen låt i den här spellistan ännu</h2>
                <p style={{ marginTop: '8px' }}>Lägg till låtar med "Lägg till"-knappen i listorna.</p>
              </div>
            ) : (
              renderSongList(player.songs.filter(s => selectedPlaylistSongs.includes(s.fileName)), { removeFromPlaylist: removeSongFromPlaylist })
            )}
          </>
        )}

        {activeView === 'chat' && (
          <ChatView
            chatMessages={chat.chatMessages} chatInput={chat.chatInput}
            setChatInput={chat.setChatInput} chatStatus={chat.chatStatus}
            chatError={chat.chatError} sendChatMessage={chat.sendChatMessage}
            chatEndRef={chat.chatEndRef} username={auth.username}
          />
        )}

        {activeView === 'admin' && auth.isAdmin && (
          <AdminView token={auth.token} username={auth.username} clearChat={chat.clearChat} />
        )}

        {activeView === 'search' && (
          <>
            <header className="library-header">
              <div className="library-icon"><Search /></div>
              <h2 style={{ flex: 1 }}>Sök</h2>
              <div className="search-container">
                <Search className="search-icon" />
                <input type="text" placeholder="Vad vill du lyssna på?" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="search-input" />
              </div>
            </header>
            {searchQuery !== '' ? (
              renderSongList(filterSongs(searchQuery))
            ) : (
              <div className="empty-view">
                <Search size={48} strokeWidth={1.4} style={{ marginBottom: '20px' }} />
                <h2>Sök efter låtar, artister eller album</h2>
              </div>
            )}
          </>
        )}
      </div>

      <PlayerBar player={player} />
    </div>
  );
}

export default App;
